import { Box, Button } from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import { useEffect } from "react";
import type { FlattenedSectionProps } from ".";
import {
  useAnswers,
  useCurrentSection,
  useQuestionnaire,
  useVisibilityMap,
} from "../../../features/questionnaire";
import { useQuestionRefs } from "../../../hooks/useQuestionRefs";
import { useSendAnswers } from "../../../hooks/useSendAnswers";
import { useShouldBlockSubmission } from "../../../hooks/useShouldBlockSubmission";
import { Question } from "./Question";

export const EditSection = ({ ref, ...props }: FlattenedSectionProps) => {
  const { questions, className } = props;
  const classes = classNames("mobius", "mobius-edit-section", className);
  const {
    sections,
    validations,
    groups,
    validateForm,
    setErrors,
    setEditMode,
    mode,
  } = useQuestionnaire();
  const visibility = useVisibilityMap();
  const { currentSectionIndex, lastViewedSectionIndex } = useCurrentSection();
  const answers = useAnswers();
  const { sendAnswers } = useSendAnswers();
  const { focusFirstError } = useQuestionRefs();
  const shouldBlockSubmission = useShouldBlockSubmission();

  useEffect(() => {
    setEditMode(true);
  }, [setEditMode]);

  const handleSaveAndContinue = () => {
    const result = validateForm({
      answers,
      sections,
      validations,
      visibility,
      groups,
      asyncErrors: {},
      currentSectionIndex,
      lastViewedSectionIndex,
      mode,
    });

    if (!result.isValid) {
      setErrors(result.errors);
      focusFirstError(result.errors);
    } else {
      setErrors({}); // Clear errors if the form is valid
      sendAnswers(answers)
        .then(() => setEditMode(false))
        .catch(err => console.error("Error sending answers:", err));
    }
  };

  return (
    <section data-testid="mobius-section" ref={ref} className={classes}>
      <div className="mobius-section__question-container">
        {questions.map(question => (
          <Question key={question.name} {...question} answers={answers} />
        ))}
      </div>
      <Box paddingBlockStart="sm" paddingBlockEnd="sm">
        <Button
          onClick={handleSaveAndContinue}
          isDisabled={shouldBlockSubmission}
        >
          Save and Continue
        </Button>
      </Box>
    </section>
  );
};

EditSection.displayName = "EditSection";
